import React from 'react';
import { C10sArea, C10sParam, C10sTexture } from '../../api/assets';

// c10s 商家数据中心的词表与小工具.
//
// 这里的 code 都是 **c10s 自己的词表** (店铺类型 / 区域形状 / 参数分类 / 加工工艺),
// 本平台字典 (mm_mode / dict) 里没有对应项, 所以只能在页面侧给中文;
// 凡是本平台字典里有的 code (材料类别、加工模式…) 一律走字典接口, 不在这里抄常量.
// 未收录的 code 一律原样显示, 不吞值.

/** 店铺类型 (t_store.store_type) */
export const STORE_TYPE_LABEL: Record<string, string> = {
    enterprise: '企业总店',
    branch: '企业分店',
    personal: '个人店铺',
};
export const storeTypeText = (t: string) => STORE_TYPE_LABEL[t] || t || '店铺已删除';

/** 可定制区域形状 (constant.Shape + 实测出现的 svg 异形, dev 全量: rectangle/circle/svg) */
export const SHAPE_LABEL: Record<string, string> = { rectangle: '矩形', circle: '圆形', svg: '异形 (SVG)' };
export const shapeText = (s: string) => SHAPE_LABEL[s] || s || '未设形状';

/** side_designs[].parameters[].type — 商家给每个加工动作各配一组参数 */
export const PARAM_TYPE_LABEL: Record<string, string> = {
    score: '轮廓雕刻',
    engrave: '填充雕刻',
    bitmap: '位图雕刻',
    cut: '切割',
    print: '打印',
    apparelPrint: '服装打印',
};
export const paramTypeText = (t: string) => PARAM_TYPE_LABEL[t] || t;

/** 加工工艺 (constant.ProcessTech) */
export const PROCESS_TECH_LABEL: Record<string, string> = {
    laser: '激光',
    print: '打印',
    'print-cut': '打印切割',
    knife: '刀切',
};
export const processTechText = (t: string) => PROCESS_TECH_LABEL[t] || t || '—';

/** 材质叠加效果 (t_texture.show_type; dev/生产全量都是 cover) */
export const TEXTURE_SHOW_TYPE_LABEL: Record<string, string> = { cover: '覆盖', multiply: '正片叠底' };
export const textureShowTypeText = (t: string) => TEXTURE_SHOW_TYPE_LABEL[t] || t || '—';

/** 商品发布状态 (constant.PublishStatus) */
export const PUBLISH_LABEL: Record<string, string> = { online: '已上架', offline: '已下架', draft: '草稿' };

/** 区域 → "矩形 81.9×35.95 mm" (不带坐标, 列表/概览用) */
export const areaText = (a: C10sArea) => `${shapeText(a.shape)} ${a.width}×${a.height} mm`;

/** 区域 → "矩形 81.9×35.95 mm @ (34.44, 13.03)" —— 坐标是区域左上角 (源侧 printArea.x/y) */
export const areaTextWithPos = (a: C10sArea) =>
    `${areaText(a)}${a.x || a.y ? ` @ (${a.x}, ${a.y})` : ''}`;

/** 商家类型标签: 企业级单独打标 (需求: 区分数据来源可信度与授权范围) */
export function MerchantBadge({ kind }: { kind: 'enterprise' | 'personal' }) {
    const ent = kind === 'enterprise';
    return (
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${ent ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
            {ent ? '企业级' : '小商家'}
        </span>
    );
}

/** 有/无加工参数 */
export function ParamBadge({ has, modes }: { has: boolean; modes?: string[] }) {
    if (!has) return <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-400 whitespace-nowrap">无</span>;
    return (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-50 text-teal-600 whitespace-nowrap"
            title={(modes || []).map(paramTypeText).join(' / ')}>
            有{modes && modes.length > 0 ? ` · ${modes.length}` : ''}
        </span>
    );
}

/** 材质 chips: 材质图 + 名称 (数据来自 t_texture 字典, 商家在面上选的) */
export function TextureChips({ items, max = 2 }: { items: C10sTexture[]; max?: number }) {
    if (!items || items.length === 0) return <span className="text-slate-300 text-[12px]">—</span>;
    const show = items.slice(0, max);
    return (
        <span className="inline-flex items-center gap-1 flex-wrap">
            {show.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded bg-teal-50 text-teal-700 text-[11px] max-w-[150px]"
                    title={`${t.title || `材质 #${t.id}`}${t.status === 'offline' ? '（已下线）' : ''}`}>
                    {t.image && <img src={t.image} alt="" className="w-3.5 h-3.5 rounded-sm object-cover" loading="lazy" />}
                    <span className="truncate">{t.title || `#${t.id}`}</span>
                </span>
            ))}
            {items.length > max && <span className="text-[11px] text-slate-400">+{items.length - max}</span>}
        </span>
    );
}

/** unix 秒 → YYYY-MM-DD HH:mm (源侧时间戳都是秒) */
export const fmtUnix = (t: number | null | undefined) => {
    if (!t) return '—';
    const d = new Date(t * 1000);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// 参数里真正有意义的几项 (源侧 Parameter 结构体字段很多, 大部分是零值/内部字段).
const PARAM_FIELDS: [string, string][] = [
    ['power', '功率 %'], ['speed', '速度 mm/s'], ['repeat', '次数'], ['density', '密度'],
    ['linesPerCm', '线密度 /cm'], ['dpi', 'DPI'], ['frequency', '频率 kHz'], ['dotDuration', '打点 μs'],
    ['bitmapMode', '图像模式'], ['bitmapScanMode', '扫描模式'], ['scanAngle', '扫描角度'],
    ['laserSourceCode', '光源'], ['processingTypeCode', '加工类型'], ['deviceCode', '设备'], ['laserHead', '加工头'],
];

// 参数字段稀疏 (激光/打印各用一套): 只列这一组参数里**至少有一条填了**的字段, 避免一排 "—".
function usedFields(parameters: C10sParam[]) {
    return PARAM_FIELDS.filter(([k]) => parameters.some((p) => {
        const v = (p.value as Record<string, unknown>)[k];
        return v !== undefined && v !== null && v !== '' && v !== 0;
    }));
}

/**
 * 加工参数表 (商品详情的规格面、订单明细的下单快照共用一份).
 * empty 是没有参数时的说明文案 —— 两处的原因不同 (商品面是没绑参数组, 订单是下单快照为空壳).
 */
export function ParamTable({ parameters, empty }: { parameters: C10sParam[]; empty?: string }) {
    const fields = React.useMemo(() => usedFields(parameters), [parameters]);
    if (parameters.length === 0) {
        return <div className="text-[11px] text-slate-400">{empty || '该面没绑参数组（加工参数取自参数组，源侧没绑就是没有）'}</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
                <thead>
                    <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100">
                        <th className="py-1.5 pr-3 whitespace-nowrap">加工动作</th>
                        <th className="py-1.5 pr-3 whitespace-nowrap">参数名</th>
                        {fields.map(([k, label]) => <th key={k} className="py-1.5 pr-3 whitespace-nowrap text-right">{label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {parameters.map((p, i) => {
                        const v = p.value as Record<string, unknown>;
                        return (
                            <tr key={`${p.type}-${i}`} className="border-b border-slate-50">
                                <td className="py-1.5 pr-3 font-medium text-slate-700 whitespace-nowrap">{paramTypeText(p.type)}</td>
                                <td className="py-1.5 pr-3 text-slate-500 max-w-[130px] truncate" title={String(v.title || '')}>{String(v.title || '—')}</td>
                                {fields.map(([k]) => {
                                    const val = v[k];
                                    const empt = val === undefined || val === null || val === '' || val === 0;
                                    return (
                                        <td key={k} className={`py-1.5 pr-3 text-right font-mono tabular-nums ${empt ? 'text-slate-300' : 'text-slate-700'}`}>
                                            {empt ? '—' : String(val)}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
